import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import styles from "./Finbox.module.scss";

import Loader from "../../components/Loader/Loader";
import FinboxAPI from "../../services/finbox.api";
import CustomButton from "../../components/Button/CustomButton";
import { setStepperDisable } from "../../redux/slices/userSlice";
import { handleShowContextualLoader, handlehideContextualLoader } from "../../utils/contextualLoaderTrigger";
import { googleEvent_LOJ227, googleEvent_LOJ228 } from "../../lib/google-analytics";
import { parseConsentHandlesStatus } from "../../lib/credeau";
import { delay } from "../../utils/utils";

export default function Finbox() {
  const [finboxError, setFinboxError] = useState(false);
  const [processingCount, setProcessingCount] = useState(0);

  const [apiStatus, setApiStatus] = useState("");

  const { upgradeLoanAmount } = useSelector((state) => state.user);

  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { finboxBankConnectAPI, bankingSurrogateAPI } = new FinboxAPI();

  const {
      aa_session_id: sessionIdFromCallback,
      success: callbackSuccess,
      error: callbackError,
      timeout: callbackTimeout,
      url,
      consent_handles_status: consentHandlesStatusRaw,
    } = Object.fromEntries(new URLSearchParams(location.search));

  const consent_handles_status = parseConsentHandlesStatus(consentHandlesStatusRaw);

  const getCallbackFailureMessage = async () => {
    let errMsg = null;
    const { consent_status: onetime_consent_status } =
      consent_handles_status?.BANK_STATEMENT_ONETIME || {};

    const { consent_status: periodic_consent_status } =
      consent_handles_status?.BANK_STATEMENT_PERIODIC || {};

    if (callbackTimeout === "true") {
      errMsg = "Bank account consent timed out. Please try again.";
    } else if (
      consentHandlesStatusRaw &&
      onetime_consent_status !== "ACCEPTED" &&
      periodic_consent_status !== "ACCEPTED"
    ) {
      errMsg = "We couldn't verify your bank account consent. Please try again.";
    } else if (
      callbackSuccess === "false" ||
      (callbackError && callbackError !== "null")
    ) {
      errMsg = "We couldn't complete your bank account consent. Please try again.";
    }
    return errMsg;
  }

  const showFinboxError = () => {
    setFinboxError(true);
    dispatch(setStepperDisable(true));
  };

  useEffect(() => {

    if (url) {
     window.location.href = url;
     return;
    }

    const checkIsCreadUFailed = async () => {
      const hasCredeauFailed = await getCallbackFailureMessage();
      if (hasCredeauFailed) {
        handleShowContextualLoader(hasCredeauFailed)
        await delay(2000)
        if (upgradeLoanAmount) {
          navigate("/select-loan");
        } else {
          navigate("/finbox-create")
        }
        handlehideContextualLoader()
        return;
      } else {
        handlefinboxBankConnectAPI();
      }
    }

    // Forward valid multi-consent statuses even when one template was rejected
    // or is still requested, so the backend can persist and validate each status.
    checkIsCreadUFailed();
    
  }, [location.search]);

  useEffect(() => {
    if(processingCount>0) {
      handleCallAgain();
    }
  }, [processingCount]);

  const handleCallAgain = () => {
    dispatch(setStepperDisable(false));
    setTimeout(() => {
      if (apiStatus === "processing") {
        handlefinboxBankConnectAPI();
      }
    }, 2000);
  };

  const handlefinboxBankConnectAPI = async () => {
    handleShowContextualLoader("Please wait...")
    const res = await finboxBankConnectAPI({
      aa_session_id: sessionIdFromCallback,
      consent_handles_status,
      success: callbackSuccess,
      error: callbackError && callbackError !== "null" ? callbackError : null,
      timeout: callbackTimeout,
    });

    if (res) {
      if (res.data.statusCode === 400) {
        dispatch(setStepperDisable(true));
        return;
      }

      if (Object.keys(res.data).length > 0) {
        if (res.data.api_status === "processing") {
          setApiStatus(res.data.api_status);

          if (processingCount < 4) {
            setProcessingCount((prev) => prev + 1);
          } else {
            googleEvent_LOJ227();
            switch (res.data.pageName) {
              case "approval":
                window.location.href = "/select-loan";
                break;
              case "dashboard":
                window.location.href = "/dashboard";
                break;
              case "rejected":
                window.location.href = "/loan-reject";
                break;
              default:
                window.location.href = "/dashboard";
                break;
            }
          }
        } else {
          // check loan amount upgrade
          if (upgradeLoanAmount) {
            handleBankingSurrogateAPI();
          } else {
            switch (res.data.pageName) {
              case "approval":
                window.location.href = "/select-loan";
                break;
              case "dashboard":
                window.location.href = "/dashboard";
                break;
              case "rejected":
                window.location.href = "/loan-reject";
                break;
              default:
                window.location.href = "/dashboard";
                break;
            }
          }
        }
      }
    } else {
      handlehideContextualLoader();
      showFinboxError();
      googleEvent_LOJ228();
    }
  };

  const handleBankingSurrogateAPI = async () => {
    const res = await bankingSurrogateAPI();
    if (res) {
      if (res?.data?.data?.final_result_from_BRE === "REJECT") {
        window.location.href = '/dashboard';
      } if(res?.data?.pageName?.toLowerCase() === "rejected") {
        window.location.href = '/loan-reject';
      } else {
        window.location.href = '/select-loan';
      }
    }
  };

  return (
    <div className="wrapper">
      {finboxError && (
        <div className={styles.finbox__error}>
          <img src="/images/alert.png" alt="" />
          <h3>Oops!</h3>
          <p>
            We are unable to fetch <br /> your bank account
          </p>

          <CustomButton
            text="Go Back"
            onClick={() => {
              // window.location.href = "/dashboard";
              navigate("/home");
            }}
          />
        </div>
      )}
    </div>
  );
}
